import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import client, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePoll } from "@/hooks/usePoll";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Users, AlertTriangle, Loader2, LogOut, LogIn, MapPin } from "lucide-react";
import { AdminClockInDialog } from "@/components/AdminClockInDialog";
import { LateClockIns } from "@/components/LateClockIns";
import { toast } from "sonner";

const hhmm = (iso) => new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
const elapsed = (iso) => {
  const mins = Math.max(0, Math.floor((Date.now() - Date.parse(iso)) / 60000));
  return `${Math.floor(mins / 60)}h ${String(mins % 60).padStart(2, "0")}m`;
};

export default function WhosClockedIn() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canStop = user?.role === "owner" || user?.role === "admin";
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [startOpen, setStartOpen] = useState(false);
  const [view, setView] = useState("site");

  const load = () => client.get("/attendance/clocked-in").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  usePoll(load, 20000);

  const clockOut = async (w) => {
    setBusy(w.workerId);
    try {
      await client.post("/attendance/admin/stop", { workerId: w.workerId });
      toast.success(`${w.name} clocked out`);
      await load();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  const workers = data?.workers || [];

  const row = (w) => (
    <Card key={w.workerId} className="p-4 border-slate-200" data-testid={`wci-row-${w.workerId}`}>
      <div className="flex items-center gap-3">
        <span className="relative flex h-3 w-3 shrink-0">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-slate-900 truncate" data-testid={`wci-name-${w.workerId}`}>{w.name}</span>
            <Badge variant="outline" className="text-emerald-700 border-emerald-200 bg-emerald-50 text-[11px]">{w.access}</Badge>
            {!w.hasLocation && <Badge variant="outline" className="text-slate-400 border-slate-200 text-[11px]">No location</Badge>}
          </div>
          <p className="text-xs text-slate-500 mt-0.5">Started {hhmm(w.startedAt)}{view === "list" && w.siteName ? ` · ${w.siteName}` : ""}</p>
          {w.idleMinutes != null && w.idleMinutes >= 60 && (
            <p className="text-xs font-semibold text-amber-600 flex items-center gap-1 mt-0.5" data-testid={`wci-idle-${w.workerId}`}>
              <AlertTriangle className="h-3.5 w-3.5" /> Idle {w.idleMinutes}m
            </p>
          )}
        </div>
        <div className="text-right shrink-0">
          <p className="font-bold text-emerald-700 text-sm" data-testid={`wci-elapsed-${w.workerId}`}>{elapsed(w.startedAt)}</p>
          {canStop && (
            <Button size="sm" className="mt-1 bg-red-500 hover:bg-red-600 h-8" data-testid={`wci-clockout-${w.workerId}`} disabled={busy === w.workerId} onClick={() => clockOut(w)}>
              {busy === w.workerId ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><LogOut className="h-3.5 w-3.5 mr-1" />Clock out</>}
            </Button>
          )}
        </div>
      </div>
    </Card>
  );

  return (
    <div className="space-y-5 max-w-2xl" data-testid="whos-clocked-in-page">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-800 transition" data-testid="wci-back">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>
      <div className="flex items-center justify-between gap-3">
        <h2 className="font-display text-2xl font-bold text-slate-900">Who's clocked in</h2>
        {canStop && (
          <Button onClick={() => setStartOpen(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="wci-clockin-btn"><LogIn className="h-4 w-4 mr-1.5" />Clock someone in</Button>
        )}
      </div>
      <AdminClockInDialog open={startOpen} onOpenChange={setStartOpen} onDone={load} clockedInIds={(data?.workers || []).map((w) => w.workerId)} />
      {canStop && <LateClockIns refreshKey={data} onClockedIn={load} />}

      <Card className="p-5 border-slate-200 flex items-center gap-4" data-testid="wci-count-card">
        <div className="h-14 w-14 rounded-full bg-orange-100 flex items-center justify-center shrink-0">
          <Users className="h-6 w-6 text-brand" />
        </div>
        <div>
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Clocked in now</p>
          <p className="font-display text-4xl font-extrabold text-slate-900" data-testid="wci-count">{data?.count ?? "—"}</p>
        </div>
      </Card>

      {workers.length > 0 && (
        <div className="flex rounded-lg border border-slate-200 p-0.5 bg-white w-fit" data-testid="wci-view-toggle">
          {[["site", "By site"], ["list", "List"]].map(([k, l]) => (
            <Button key={k} size="sm" variant="ghost" onClick={() => setView(k)} data-testid={`wci-view-${k}`}
              className={view === k ? "bg-brand text-white hover:bg-brand-dark hover:text-white" : "text-slate-600"}>{l}</Button>
          ))}
        </div>
      )}

      {workers.length === 0 ? (
        <Card className="p-8 text-center text-slate-400" data-testid="wci-empty">Nobody is clocked in right now.</Card>
      ) : view === "site" ? (
        Object.entries(workers.reduce((acc, w) => { const k = w.siteName && w.siteName !== "—" ? w.siteName : "Field / no site"; (acc[k] = acc[k] || []).push(w); return acc; }, {}))
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([site, ws]) => (
            <section key={site} className="space-y-2" data-testid={`wci-site-${site}`}>
              <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                <MapPin className="h-4 w-4 text-brand" />{site}
                <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">{ws.length} on site</Badge>
              </h3>
              {ws.map(row)}
            </section>
          ))
      ) : workers.map(row)}
    </div>
  );
}
